import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import { useCurrency } from '../contexts/CurrencyContext';
import { usePrices } from '../hooks/usePrices';
import TokenIcon from '../components/TokenIcon';
import PublicTokenIcon from '../components/PublicTokenIcon';
import CoinChart from '../components/CoinChart';
import CoinDataTab from '../components/CoinDataTab';
import ExitStrategyTab from '../components/ExitStrategyTab';
import KeyStatistics from '../components/KeyStatistics';
import MarketData from '../components/MarketData';
import WelcomeHero from '../components/WelcomeHero';
import api from '../services/api';
import publicApi from '../services/publicApi';

function CoinPage() {
  const { coinSymbol } = useParams();
  const { navigateWithLang } = useLanguageNavigation();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const { selectedCurrency, currencies, convertValue } = useCurrency();
  const [coinData, setCoinData] = useState(null);
  const [rawHoldings, setRawHoldings] = useState(null);
  const [holdings, setHoldings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [portfolioGains, setPortfolioGains] = useState({});
  const [loadingGains, setLoadingGains] = useState(false);
  const [hasAnalysisData, setHasAnalysisData] = useState(false);

  // Memoize the symbols array to prevent usePrices from re-running
  const symbols = useMemo(() => [coinSymbol], [coinSymbol]);

  // Fetch current price for this coin
  const { prices, loading: pricesLoading } = usePrices(symbols, false, 'CoinPage');
  const currentPrice = prices?.[coinSymbol]?.usd || 0;

  // console.log(`[CoinPage] Render - coinSymbol: ${coinSymbol}, currentPrice: ${currentPrice}, rawHoldings:`, rawHoldings);

  const formatCurrency = (value) => {
    if (value === 0) return '$0.00';
    if (typeof value !== 'number' || isNaN(value)) {
      return '$0.00';
    }
    const absValue = Math.abs(value);
    if (absValue < 0.01 && value !== 0) {
      return `$${value.toFixed(6)}`;
    } else if (absValue >= 1 || value <= -1) {
      return `$${value.toFixed(2)}`;
    } else {
      return `$${value.toFixed(4)}`;
    }
  };

  const formatPercentage = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Fetch portfolio gains for this coin
  const fetchPortfolioGains = async () => {
    if (!coinSymbol || !isLoggedIn) return;
    
    setLoadingGains(true);
    try {
      const response = await api.get('/api/wallets/portfolio-gains');
      const gainsMap = {};
      if (response.data && response.data.gains) {
        response.data.gains.forEach(gain => {
          gainsMap[gain.coin] = gain;
        });
      }
      setPortfolioGains(gainsMap);
    } catch (error) {
      console.error('Error fetching portfolio gains:', error);
      // Fallback: calculate basic gains from holdings
      await calculateBasicGains();
    } finally {
      setLoadingGains(false);
    }
  };

  // Fallback function to calculate basic gains from holdings data
  const calculateBasicGains = async () => {
    if (!holdings || !currentPrice) return;
    
    try {
      const avgBuyPrice = holdings.avgBuyPrice || 0;
      const totalCost = holdings.totalCost || 0;
      const totalValue = holdings.totalValue || 0;
      
      if (avgBuyPrice > 0 && totalValue > 0) {
        const totalGain = totalValue - totalCost;
        const totalGainPercent = avgBuyPrice > 0 ? ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;
        
        const gainsMap = {
          [coinSymbol.toUpperCase()]: {
            coin: coinSymbol.toUpperCase(),
            gain_24h: 0, // Can't calculate without historical data
            gain_24h_percent: 0,
            gain_1m: 0, // Can't calculate without historical data
            gain_1m_percent: 0,
            gain_all_time: totalGain,
            gain_all_time_percent: totalGainPercent
          }
        };
        
        setPortfolioGains(gainsMap);
      }
    } catch (error) {
      console.error('Error calculating basic gains:', error);
    }
  };

  // Calculate coin-specific gains
  const coinGains = useMemo(() => {
    if (!portfolioGains || !coinSymbol) {
      return { gain24h: 0, gain24hPercent: 0, gain1m: 0, gain1mPercent: 0, gainAllTime: 0, gainAllTimePercent: 0 };
    }
    
    const gains = portfolioGains[coinSymbol.toUpperCase()] || {};
    return {
      gain24h: gains.gain_24h || 0,
      gain24hPercent: typeof gains.gain_24h_percent === 'number' ? gains.gain_24h_percent : 0,
      gain1m: gains.gain_1m || 0,
      gain1mPercent: typeof gains.gain_1m_percent === 'number' ? gains.gain_1m_percent : 0,
      gainAllTime: gains.gain_all_time || 0,
      gainAllTimePercent: typeof gains.gain_all_time_percent === 'number' ? gains.gain_all_time_percent : 0,
    };
  }, [portfolioGains, coinSymbol]);

  // Calculate total market value for this coin
  const totalMarketValue = useMemo(() => {
    if (!holdings || !currentPrice) return 0;
    return holdings.totalValue || 0;
  }, [holdings, currentPrice]);

  useEffect(() => {
    fetchPortfolioGains();
  }, [coinSymbol, isLoggedIn]);

  // Check if analysis data exists for this coin
  useEffect(() => {
    const checkAnalysisData = async () => {
      try {
        // Use public API for analysis data check (it's safe for non-authenticated users)
        const response = await publicApi.get(`/api/public/coins/${coinSymbol}/data`);
        setHasAnalysisData(response.data && response.data.exists);
      } catch (error) {
        setHasAnalysisData(false);
      }
    };
    
    if (coinSymbol) {
      checkAnalysisData();
    }
  }, [coinSymbol]);

  // Recalculate gains when holdings or current price changes
  useEffect(() => {
    if (holdings && currentPrice && Object.keys(portfolioGains).length === 0) {
      calculateBasicGains();
    }
  }, [holdings, currentPrice, portfolioGains]);

  useEffect(() => {
    // console.log(`[CoinPage] Main useEffect triggered - coinSymbol: ${coinSymbol}`);
    const fetchCoinData = async () => {
      if (!coinSymbol) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch coin details from the appropriate API
        let coin;
        if (isLoggedIn) {
          // Use authenticated API that includes user-specific data
          const response = await api.get(`/api/coins?symbol=${coinSymbol}`);
          coin = response.data.find(c => c.symbol.toUpperCase() === coinSymbol.toUpperCase());
        } else {
          // Use public API for non-authenticated users
          const response = await publicApi.get(`/api/public/coins/${coinSymbol}`);
          coin = response.data;
        }
        
        if (!coin) {
          setError(`Coin ${coinSymbol} not found`);
          setLoading(false);
          return;
        }

        setCoinData(coin);

        // Fetch holdings for this coin (only if logged in)
        if (isLoggedIn) {
          try {
            const holdingsResponse = await api.get(`/api/wallets/balances?coin=${coinSymbol}`);
            // Use case-insensitive comparison for holdings lookup
            const coinHoldings = holdingsResponse.data.find(h => h.coin.toUpperCase() === coinSymbol.toUpperCase());
            
            // console.log(`[CoinPage] Holdings response for ${coinSymbol}:`, holdingsResponse.data);
            // console.log(`[CoinPage] Found coin holdings:`, coinHoldings);
            
            if (coinHoldings) {
              setRawHoldings(coinHoldings);
            } else {
              // console.log(`[CoinPage] No holdings found for ${coinSymbol}`);
              setRawHoldings(null);
              setHoldings(null);
            }
          } catch (holdingsError) {
            console.error(`[CoinPage] Error fetching holdings for ${coinSymbol}:`, holdingsError);
            setRawHoldings(null);
            setHoldings(null);
          }
        } else {
          // Clear holdings for non-logged users
          setRawHoldings(null);
          setHoldings(null);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching coin data:', err);
        setError(err.message || 'Failed to fetch coin data');
        setLoading(false);
      }
    };

    fetchCoinData();
  }, [coinSymbol, isLoggedIn]); // Added isLoggedIn dependency

  // Calculate holdings values using useMemo to prevent infinite loops
  const calculatedHoldings = useMemo(() => {
    // console.log(`[CoinPage] useMemo triggered - currentPrice: ${currentPrice}, rawHoldings:`, rawHoldings);
    
    if (rawHoldings && currentPrice > 0) {
      const balance = Number(rawHoldings.balance || 0);
      const avgBuyPrice = Number(rawHoldings.avg_buy_price || 0);
      const totalValue = balance * currentPrice;
      const totalCost = balance * avgBuyPrice;
      const profitLoss = totalValue - totalCost;
      const profitLossPercentage = avgBuyPrice > 0 ? ((currentPrice - avgBuyPrice) / avgBuyPrice) * 100 : 0;

      const holdingsData = {
        balance,
        avgBuyPrice,
        totalValue: Number(totalValue),
        totalCost: Number(totalCost),
        profitLoss: Number(profitLoss),
        profitLossPercentage: Number(profitLossPercentage)
      };

      // console.log(`[CoinPage] Calculated holdings with price ${currentPrice}:`, holdingsData);
      return holdingsData;
    }
    
    return null;
  }, [currentPrice, rawHoldings?.balance, rawHoldings?.avg_buy_price]);

  // Update holdings state when calculatedHoldings changes
  useEffect(() => {
    // console.log(`[CoinPage] Setting holdings state:`, calculatedHoldings);
    setHoldings(calculatedHoldings);
  }, [calculatedHoldings]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('coin.error', { error })}
        </Alert>
        <Typography variant="body1" color="textSecondary">
                          <a href="#" onClick={() => navigateWithLang('portfolio')} style={{ color: 'inherit' }}>
            ← {t('coin.goBack')}
          </a>
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2, md: 4 }, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                          <a href="#" onClick={() => navigateWithLang('portfolio')} style={{ color: 'inherit', textDecoration: 'none' }}>
            ← {t('common.back')}
          </a>
        </Typography>
        
        {/* Show WelcomeHero for non-authenticated users below the back navigation */}
        {!isLoggedIn && (
          <Box sx={{ 
            mb: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <WelcomeHero />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isLoggedIn ? (
              <TokenIcon coinSymbol={coinSymbol} size={48} />
            ) : (
              <PublicTokenIcon coinSymbol={coinSymbol} size={48} />
            )}
            <Box>
              <Typography variant="h3" component="span" sx={{ fontSize: { xs: '2.1rem', sm: '2.5rem' }, fontWeight: 700 }}>
                {coinSymbol}
              </Typography>
            </Box>
          </Box>
          {isLoggedIn && holdings && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: { xs: '2.1rem', sm: '2.5rem' } }}>
                ${Math.round(totalMarketValue).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Balance and Price Row */}
        {isLoggedIn && holdings && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.primary" sx={{ ml: 8, fontWeight: 700 }}>
              {(() => {
                const balance = Number(holdings.balance || 0);
                // If the fractional part is significant (> 0.0001), show up to 6 decimal places
                // Otherwise, show as integer
                const fractionalPart = balance % 1;
                const isSignificantFraction = fractionalPart > 0.0001;
                return balance.toLocaleString(undefined, { 
                  maximumFractionDigits: isSignificantFraction ? 6 : 0,
                  minimumFractionDigits: 0
                });
              })()}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
              {t('common.price')}: {pricesLoading ? <CircularProgress size={12} /> : formatCurrency(currentPrice)}
            </Typography>
          </Box>
        )}
        
        {/* Show price for non-logged users */}
        {!isLoggedIn && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
              {t('common.price')}: {pricesLoading ? <CircularProgress size={12} /> : formatCurrency(currentPrice)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Financial Summary Section */}
      {isLoggedIn && holdings && (
        <Box sx={{ position: 'relative', mb: 4, maxWidth: { xs: '100%', md: 1200 }, mx: { xs: 0, md: 'auto' }, pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            {/* 24H Gain */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color:
                  (coinGains.gain24h || 0) > 0
                    ? 'success.main'
                    : (coinGains.gain24h || 0) < 0
                    ? 'error.main'
                    : 'text.secondary',
                fontSize: '1.05rem',
                lineHeight: 1.2,
              }}
            >
              {loadingGains ? (
                <CircularProgress size={16} />
              ) : (
                `${coinGains.gain24h >= 0 ? '+' : ''}$${Math.round(coinGains.gain24h).toLocaleString()} (${coinGains.gain24hPercent >= 0 ? '+' : ''}${isFinite(coinGains.gain24hPercent) ? coinGains.gain24hPercent.toFixed(1) : '0.0'}%)`
              )}{' '}
              <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>24H</span>
            </Typography>
            {/* 1M Gain */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color:
                  (coinGains.gain1m || 0) > 0
                    ? 'success.main'
                    : (coinGains.gain1m || 0) < 0
                    ? 'error.main'
                    : 'text.secondary',
                fontSize: '1.05rem',
                lineHeight: 1.2,
              }}
            >
              {loadingGains ? (
                <CircularProgress size={16} />
              ) : (
                `${coinGains.gain1m >= 0 ? '+' : ''}$${Math.round(coinGains.gain1m).toLocaleString()} (${coinGains.gain1mPercent >= 0 ? '+' : ''}${isFinite(coinGains.gain1mPercent) ? coinGains.gain1mPercent.toFixed(1) : '0.0'}%)`
              )}{' '}
              <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>1M</span>
            </Typography>
            {/* All-Time Gain */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color:
                  (coinGains.gainAllTime || 0) > 0
                    ? 'success.main'
                    : (coinGains.gainAllTime || 0) < 0
                    ? 'error.main'
                    : 'text.secondary',
                fontSize: '1.05rem',
                lineHeight: 1.2,
              }}
            >
              {loadingGains ? (
                <CircularProgress size={16} />
              ) : (
                `${coinGains.gainAllTime >= 0 ? '+' : ''}$${Math.round(coinGains.gainAllTime).toLocaleString()} (${coinGains.gainAllTimePercent >= 0 ? '+' : ''}${isFinite(coinGains.gainAllTimePercent) ? coinGains.gainAllTimePercent.toFixed(1) : '0.0'}%)`
              )}{' '}
              <span style={{ fontWeight: 400, color: 'inherit', fontSize: '0.98em' }}>All time</span>
            </Typography>
          </Box>
        </Box>
      )}

      {/* Chart Section */}
      <Box sx={{ mb: 4 }}>
        <CoinChart symbol={coinSymbol} avgBuyPrice={isLoggedIn ? holdings?.avgBuyPrice : null} />
      </Box>

      {/* Key Statistics Section */}
      <KeyStatistics coinSymbol={coinSymbol} />

      {/* Market Data Section */}
      {hasAnalysisData && (
        <MarketData coinSymbol={coinSymbol} />
      )}

      {/* Tabs for logged in users */}
      {isLoggedIn && hasAnalysisData && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="coin tabs">
            <Tab label={t('coin.exitStrategies')} />
            <Tab label={t('coin.analysis.title')} />
          </Tabs>
        </Box>
      )}

      {/* Tab Content for logged in users */}
      {isLoggedIn && hasAnalysisData && activeTab === 0 && (
        <ExitStrategyTab coinSymbol={coinSymbol} currentPrice={currentPrice} balance={holdings?.balance} />
      )}

      {isLoggedIn && hasAnalysisData && activeTab === 1 && (
        <CoinDataTab symbol={coinSymbol} />
      )}

      {/* Show Exit Strategy directly if logged in and no analysis data */}
      {isLoggedIn && !hasAnalysisData && (
        <ExitStrategyTab coinSymbol={coinSymbol} currentPrice={currentPrice} balance={holdings?.balance} />
      )}

      {/* Show analysis data for non-logged users if available */}
      {!isLoggedIn && hasAnalysisData && (
        <CoinDataTab symbol={coinSymbol} />
      )}

      {/* Show message for non-logged users when no analysis data */}
      {!isLoggedIn && !hasAnalysisData && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('coin.loginForMoreFeatures', { defaultValue: 'Sign in to access portfolio tracking and exit strategies' })}
          </Typography>
        </Box>
      )}
    </Container>
  );
}

export default CoinPage; 